import { getPreviewFileType, isPreviewable, type PreviewFile } from '../../../components/file-preview/types'

/** The subset of a listed document the in-app viewer needs (Freelo #32223790). */
export interface PreviewableDocument {
  id: string
  name: string
  mimetype: string
  size?: number | null
  previewUrl?: string | null
  currentRevision?: {
    fileId?: string | null
    downloadUrl?: string | null
    file?: { size?: number | null } | null
  } | null
}

/** A document opens in the viewer when its type is supported (PDF, image, spreadsheet) and there is
 * something to load: a signed preview URL, or the file id for the same-origin fetch. Word and other
 * types have no viewer and fall back to the detail dialog. */
export const canPreviewDocument = (doc: PreviewableDocument): boolean =>
  !!(doc.previewUrl || doc.currentRevision?.fileId) && isPreviewable(doc.mimetype)

export const toPreviewFile = (doc: PreviewableDocument): PreviewFile => ({
  url: doc.previewUrl || '',
  title: doc.name,
  downloadUrl: doc.currentRevision?.downloadUrl ?? undefined,
  type: getPreviewFileType(doc.mimetype)!,
  fileId: doc.currentRevision?.fileId ?? undefined,
  fileSize: doc.currentRevision?.file?.size ?? doc.size ?? undefined,
})

export interface PreviewIndex {
  files: PreviewFile[]
  indexById: Map<string, number>
}

/** The viewer's file list (in list order, so prev/next follow the listing) and the id → index map. */
export const buildPreviewIndex = (documents: PreviewableDocument[]): PreviewIndex => {
  const files: PreviewFile[] = []
  const indexById = new Map<string, number>()
  documents.forEach((doc) => {
    if (!canPreviewDocument(doc)) return
    indexById.set(doc.id, files.length)
    files.push(toPreviewFile(doc))
  })
  return { files, indexById }
}
